/**
 * Historical Arrears tiles when the month was never captured (28 Sep 2026).
 *
 * A Historical window reads the arrears frozen at its end month. A site with
 * capture switched off has no such snapshot, and the tiles printed £0 over
 * hundreds of thousands of open arrears — a zero nobody measured. The API now
 * says where the figure comes from (`arrears_value_source`); on `'none'` the
 * tiles print this instead of a number.
 */

/**
 * Where a Historical Arrears figure comes from: `'none'` = the month-end was never captured;
 * `'empty'` (CR-8660) = not captured but between two captured months, so a zero balance.
 */
export type ArrearsValueSource = 'snapshot' | 'none' | 'live' | 'empty';

/** Printed in place of the value when there is no snapshot. */
export const NO_ARREARS_SNAPSHOT = 'No snapshot';

/** One line under it. */
export const NO_ARREARS_SNAPSHOT_FOOT = 'Month-end not captured';

/** Hover text: what happened and where the current figure is. */
export const NO_ARREARS_SNAPSHOT_HINT =
  'No month-end arrears snapshot was captured for the end of this period, so there is no historical figure. '
  + "Switch Data to Live to see today's arrears.";

/**
 * The Historical Arrears Value tile's drill-down filters: the month the tile shows.
 *
 * CR-7270 rework (Sarah's retest, 16 Oct 2026): the tile reads the month-end
 * snapshot at the end of the dashboard window, but its drill dropped the window,
 * so the list was TODAY's live arrears under a frozen figure. Whenever the tile is
 * not live the window travels and the server answers the same month from it; a
 * live tile counts today's whole stock, which a windowed drill would narrow, and a
 * response that does not say keeps the old unwindowed drill.
 *
 * @param withWindow - the dashboard's drill-down filters, window included
 * @param withoutWindow - the same filters without `date_from` / `date_to`
 * @param source - where the tile's figure came from (`arrears_value_source`)
 * @returns the filters the tile's drill-down sends
 */
export function arrearsTileDrillFilters(
  withWindow: Record<string, string>,
  withoutWindow: Record<string, string>,
  source?: ArrearsValueSource,
): Record<string, string> {
  return source && source !== 'live' ? withWindow : withoutWindow;
}
